import React, { ReactText } from 'react';
import { Box, Checkbox, TableRow } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import LoaderRow from '../../../../../ui-kit/Table/LoaderRow';
import { HoverLink, TableCell, Typography } from '../../../../../ui-kit';
import { routePaths } from '../../../../../routePaths';
import { useWarehouseCardAction } from '../../../providers/WarehouseCardActionProvider';
import { PaginatedStockCardsQuery } from '../../../../../generated/graphql';
import { useWarehousesScope } from '../../../providers/WarehouseScopeProvider';
import { usePriceService } from '../../../../../utils/hooks/priceService';
import { useUser } from '../../../../../utils/hooks/auth';
import { headCells, mapStockCardsToRows } from './utils';

type StockCard = PaginatedStockCardsQuery['paginatedStockCards']['items'][0];

interface TableContentProps {
    isLoading?: boolean;
    rowsPerPage: number;
    stockCards: StockCard[];
}

const TableContent = ({ isLoading, rowsPerPage, stockCards }: TableContentProps) => {
    const { t } = useTranslation();
    const { currentWarehouseId } = useWarehousesScope();
    const { priceService } = usePriceService();
    const { user: { defaultCurrency: currency } } = useUser();
    const location = useLocation();
    const { currentAction, selectedCardIds, setSelectedCardIds } = useWarehouseCardAction();
    const isSelected = (id: string) => selectedCardIds.includes(id);
    const handleCheckBoxClick = (id: string) => {
        const selectionSet = new Set<string>();
        selectedCardIds.forEach(cardId => {
            selectionSet.add(cardId);
        });

        const hasStockCardId = selectionSet.has(id);

        if(!hasStockCardId){
            selectionSet.add(id);
        } else {
            selectionSet.delete(id);
        }
        const newSelected = Array.from(selectionSet.values());

        setSelectedCardIds(newSelected);
    };

    const rows = mapStockCardsToRows({
        currentWarehouseId: currentWarehouseId as string,
        priceService,
        currencyCode: currency.currencyCode,
    })(stockCards);

    return isLoading
        ? <LoaderRow columnCount={headCells.length} itemsPerPage={rowsPerPage} itemHeight={56} />
        : (
            <>
                {rows.map(row => (
                    <TableRow tabIndex={-1} key={row.id}>
                        {headCells.map(({ id, align }) => {
                        const isFirstElement = id === 'name';
                        let cellValue: ReactText | JSX.Element = row[id];
                        const shouldTranslate = id === 'convertedLeftOvers';

                        if (shouldTranslate) {
                            cellValue = t(cellValue as string) as ReactText;
                        }

                        const component = isFirstElement ? 'th' : '';
                        const rowStyle = isFirstElement ? 'row' : '';
                        let content = isFirstElement
                        ? (
                            <Typography fontWeight='medium'>
                                <HoverLink
                                    wide
                                    to={{
                                        pathname:`${routePaths.warehouseCards}/${row.id}`,
                                        state: { referrer: location },
                                    }}
                                >
                                    {row[id]}
                                </HoverLink>
                            </Typography>
                        )
                        : cellValue;

                        if (!!currentAction && isFirstElement) {
                            content = (
                                <Box display="flex" alignItems="center">
                                    <Checkbox
                                        style={{ padding: '0px', marginRight: '8px' }}
                                        color='primary'
                                        name={row.id}
                                        checked={isSelected(row.id)}
                                        onClick={() => handleCheckBoxClick(row.id)}
                                    />
                                    {content}
                                </Box>
                            );
                        }

                        return (
                            <TableCell
                                row={rowStyle}
                                // @ts-ignore
                                component={component}
                                isSticky={isFirstElement}
                                key={id} id={id} align={align}
                            >
                                {content}
                            </TableCell>
                        );
                    })}
                    </TableRow>
                ))}
            </>
        );
};

export default TableContent;
